import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';

const PlayerAnnouncements = () => {
  const navigate = useNavigate();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/announcements`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setAnnouncements(data);
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
      toast.error('Failed to load announcements');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (announcement) => {
    if (announcement.is_read) return;

    try {
      await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/announcements/${announcement.id}/read`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      // Update local state
      setAnnouncements(prev => 
        prev.map(a => a.id === announcement.id ? { ...a, is_read: true } : a)
      );
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleViewAnnouncement = (announcement) => {
    setSelectedAnnouncement(announcement);
    setDialogOpen(true);
    markAsRead(announcement);
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'Normal': 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 border-gray-200 dark:border-gray-700',
      'Important': 'bg-amber-100 text-amber-800 border-amber-200',
      'Urgent': 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[priority] || colors['Normal'];
  };

  const getPriorityIcon = (priority) => {
    const icons = {
      'Normal': '📢',
      'Important': '⚠️',
      'Urgent': '🚨'
    };
    return icons[priority] || '📢';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const unreadCount = announcements.filter(a => !a.is_read).length;

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Announcements
            </h2>
            <p className="text-gray-600 dark:text-gray-500">
              Stay updated with the latest news from the academy
              {unreadCount > 0 && (
                <span className="ml-2 text-emerald-600 font-medium">
                  ({unreadCount} unread)
                </span>
              )}
            </p>
          </div>

          {announcements.length === 0 ? (
          <Card>
            <CardContent className="py-12">
              <p className="text-center text-gray-500 dark:text-gray-500">No announcements available</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {announcements.map(announcement => (
              <Card 
                key={announcement.id} 
                className={`cursor-pointer transition-all hover:shadow-md ${
                  announcement.is_pinned ? 'border-emerald-300 bg-emerald-50/30' : ''
                } ${!announcement.is_read ? 'border-l-4 border-l-emerald-500' : ''}`}
                onClick={() => handleViewAnnouncement(announcement)}
                data-testid={`announcement-card-${announcement.id}`}
              >
                <CardContent className="py-4">
                  <div className="flex items-start gap-4">
                    <div className="text-2xl">
                      {getPriorityIcon(announcement.priority)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {announcement.is_pinned && (
                          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
                            📌 Pinned
                          </Badge>
                        )}
                        <h4 className={`font-semibold ${!announcement.is_read ? 'text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-300'}`}>
                          {announcement.title}
                        </h4>
                        <Badge className={getPriorityColor(announcement.priority)}>
                          {announcement.priority}
                        </Badge>
                        {announcement.category && announcement.category !== 'General' && (
                          <Badge
                            className={
                              announcement.category === 'Training'
                                ? 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800'
                                : 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800'
                            }
                            data-testid={`category-badge-${announcement.id}`}
                          >
                            {announcement.category}
                          </Badge>
                        )}
                        {!announcement.is_read && (
                          <Badge className="bg-emerald-500 text-white">New</Badge>
                        )}
                      </div>
                      <p className="text-gray-600 dark:text-gray-500 text-sm line-clamp-2 mb-2">
                        {announcement.message}
                      </p>
                      <div className="flex gap-4 text-xs text-gray-500 dark:text-gray-500">
                        <span>👤 {announcement.created_by_name}</span>
                        <span>📅 {formatDate(announcement.created_at)}</span>
                        {announcement.attachment_name && (
                          <span>📎 Attachment</span>
                        )}
                      </div>
                    </div>
                    <div className="text-gray-400 dark:text-gray-500">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* View Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {selectedAnnouncement?.is_pinned && <span>📌</span>}
                {selectedAnnouncement?.title}
              </DialogTitle>
            </DialogHeader>
            {selectedAnnouncement && (
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Badge className={getPriorityColor(selectedAnnouncement.priority)}>
                    {selectedAnnouncement.priority}
                  </Badge>
                </div>
                
                <p className="whitespace-pre-wrap text-gray-700 dark:text-gray-300">
                  {selectedAnnouncement.message}
                </p>
                
                <div className="text-sm text-gray-500 dark:text-gray-500 space-y-1">
                  <p>👤 From: {selectedAnnouncement.created_by_name}</p>
                  <p>📅 Posted: {formatDate(selectedAnnouncement.created_at)}</p>
                  {selectedAnnouncement.expires_at && (
                    <p>⏰ Expires: {formatDate(selectedAnnouncement.expires_at)}</p>
                  )}
                </div>

                {selectedAnnouncement.attachment_url && (
                  <div className="pt-2 border-t">
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}${selectedAnnouncement.attachment_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-emerald-600 hover:underline"
                    >
                      📎 Download: {selectedAnnouncement.attachment_name}
                    </a>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
        </div>
      </div>
    </Layout>
  );
};

export default PlayerAnnouncements;
